export interface GoalAlignment {
  goalId: string;
  alignment: string;
}

export interface ActivityInput {
  hoursPerMonth: number;
  energyImpact: number;
  important: boolean;
  urgent: boolean;
  enjoymentAbility: string;
  complexity: string;
  revenueImpact: string;
  strategicPillar: string;
  goalAlignments: GoalAlignment[];
  isMandatory?: boolean;
}

export interface ScoreBreakdown {
  revenueScore: number;
  goalScore: number;
  enjoymentScore: number;
  energyScore: number;
  importanceScore: number;
  complexityModifier: number;
  comboTotal: number;
  total: number;
}

export interface ScoreFlags {
  isZoneOfGenius: boolean;
  isEnergyDrain: boolean;
  isHighHoursBurden: boolean;
  isUrgentDelegate: boolean;
  isOperationalOnly: boolean;
  isDefinitiveDrop: boolean;
}

export interface ScoreResult {
  score: number;
  decision: "DO YOURSELF" | "PLAN" | "DELEGATE" | "DROP" | "MUST_DELEGATE";
  breakdown: ScoreBreakdown;
  flags: ScoreFlags;
}

// =============================================================================
// Stage detection — classifies the session by team size × revenue band.
// Used to choose stage-aware thresholds in scoreDelegationActivity below.
// =============================================================================

export type Stage = "solo" | "small_team" | "mid" | "enterprise";

export interface SessionContext {
  team_size_band: string | null;
  gross_revenue_band: string | null;
}

// Schema-pinned band-set predicates. If new bands are added in the future,
// update these in one place rather than tweaking detectStage inline.
const LARGE_TEAM = ["11_25", "26_100", "over_100"];
const MID_TEAM = ["4_10", "11_25"];
const SMALL_TEAM = ["1_3"];
const HIGH_REV = ["2m_10m", "10m_50m", "over_50m"];
const MID_REV = ["500k_2m", "2m_10m"];
const SOLO_MID_REV = ["100k_500k", "500k_2m"]; // for the "well-funded solo" carve-out

export function detectStage(session: SessionContext): Stage {
  const team = session.team_size_band;
  const rev = session.gross_revenue_band;

  // Enterprise: 11+ people AND $2M+ revenue. Both required — a 100-person
  // pre-revenue ngo is structurally not enterprise; a $50M solo isn't either.
  if (LARGE_TEAM.includes(team || "") && HIGH_REV.includes(rev || "")) {
    return "enterprise";
  }

  // Mid: 4-25 people, OR 1-3 people with $500K+ revenue (the under-capitalized
  // small team that's already at mid-market scale economically).
  if (
    MID_TEAM.includes(team || "") ||
    (SMALL_TEAM.includes(team || "") && MID_REV.includes(rev || ""))
  ) {
    return "mid";
  }

  // Small team: 1-3 people, OR solo at >=$100K revenue (the well-funded
  // solo who has resources to hire even without a team yet).
  if (SMALL_TEAM.includes(team || "") || (team === "solo" && SOLO_MID_REV.includes(rev || ""))) {
    return "small_team";
  }

  // Default: solo. Catches solo + (under_100k | pre_revenue), and sessions
  // with null bands (legacy or test sessions). Most conservative default.
  return "solo";
}

// =============================================================================
// FounderStage — narrative-side stage classification (separate from the
// scoring-engine Stage above).
//
// detectStage above serves the scoring engine: maps to four threshold buckets
// (solo/small_team/mid/enterprise) used to tune the Delegate cutoff. Different
// concern from what the AI narrative needs.
//
// The override's AI report uses a different 4-tier rubric tied to action
// recommendations: Build-the-Team / First-Hire / Team-Building / Optimization.
// classifyFounderStage below maps (revenue, team) → one of those four labels
// deterministically. Used by route.ts to inject a "Stage:" line into the
// AI prompt; the override instructs the AI to narrate the provided value
// rather than re-derive it (the AI got it wrong empirically).
// =============================================================================

export type FounderStage = "Build-the-Team" | "First-Hire" | "Team-Building" | "Optimization";

// Per-band implied stage. Used as the base mapping before overrides apply.
const REVENUE_TO_STAGE: Record<string, FounderStage> = {
  pre_revenue: "Build-the-Team",
  under_100k: "Build-the-Team",
  "100k_500k": "First-Hire",
  "500k_2m": "Team-Building",
  "2m_10m": "Optimization",
  "10m_50m": "Optimization",
  over_50m: "Optimization",
};
const TEAM_TO_STAGE: Record<string, FounderStage> = {
  solo: "Build-the-Team",
  "1_3": "First-Hire",
  "4_10": "Team-Building",
  "11_25": "Optimization",
  "26_100": "Optimization",
  over_100: "Optimization",
};

/**
 * Rule: TEAM-DOMINANT (team size determines stage), with two carve-outs:
 *   (a) solo carve-out — solo founder + revenue >= $100K → First-Hire
 *       Rationale: a solo at $100K+ is past the "prove revenue" gate;
 *       Build-the-Team advice ("don't hire") is wrong because revenue
 *       exists. They should make their first hire.
 *   (b) pre-revenue override — 4+ team + pre_revenue → Build-the-Team
 *       Rationale: a stealth-mode team or co-founders without revenue
 *       should focus on revenue, not hire ordering. Team-Building or
 *       higher advice ("sequence more hires") misfires here.
 *
 * Null/missing/unknown bands → Build-the-Team (conservative default).
 */
export function classifyFounderStage(
  revenue: string | null | undefined,
  team: string | null | undefined,
): FounderStage {
  if (!revenue || !team) return "Build-the-Team";
  const teamStage = TEAM_TO_STAGE[team];
  const revStage = REVENUE_TO_STAGE[revenue];
  if (!teamStage || !revStage) return "Build-the-Team";

  // (b) pre-revenue override — applies before team-dominant rule
  if (revenue === "pre_revenue" && team !== "solo" && team !== "1_3") {
    return "Build-the-Team";
  }
  // (a) solo carve-out
  if (team === "solo" && revenue !== "pre_revenue" && revenue !== "under_100k") {
    return "First-Hire";
  }
  // TEAM-DOMINANT default
  return teamStage;
}

// Stage-aware Delegate threshold. Keep (>=25) and Plan (>=18) thresholds are
// unchanged across stages — only the Delegate floor moves. Solo raises it to
// avoid recommending delegation lightly when there's no team to delegate to;
// enterprise lowers it to push more work off the CEO's plate at scale.
function getDelegateThreshold(stage: Stage): number {
  switch (stage) {
    case "solo":
      return 10;
    case "small_team":
      return 8;
    case "mid":
      return 7;
    case "enterprise":
      return 6;
  }
}

export function scoreDelegationActivity(
  input: ActivityInput,
  session: SessionContext,
): ScoreResult {
  // Validation
  if (input.energyImpact !== undefined && (input.energyImpact < -2 || input.energyImpact > 2)) {
    throw new Error(
      `Invalid energyImpact: must be between -2 and +2. Received: ${input.energyImpact}`,
    );
  }

  // 1. revenueScore (range: 0 to 10)
  let revenueScore = 0;
  if (input.revenueImpact === "High") revenueScore = 10;
  else if (input.revenueImpact === "Mid") revenueScore = 6;
  else if (input.revenueImpact === "Low") revenueScore = 2;
  // N/A defaults to 0

  // 2. goalScore (range: -1 to 15)
  let goalScore = 0;
  if (input.goalAlignments && input.goalAlignments.length > 0) {
    let alignSum = 0;
    input.goalAlignments.forEach((g) => {
      if (g.alignment === "Strongly Aligned" || g.alignment === "Strongly") alignSum += 4;
      // "Somewhat Aligned" is the canonical value going forward (fixed typo). Legacy
      // sessions stored "Somehow Aligned" / "Somehow" — accepted as a transitional
      // back-compat shim. No DB migration; removable once legacy sessions age out.
      else if (
        g.alignment === "Somewhat Aligned" ||
        g.alignment === "Somewhat" ||
        g.alignment === "Somehow Aligned" ||
        g.alignment === "Somehow"
      )
        alignSum += 2;
    });
    goalScore = Math.min(alignSum, 15);
  } else {
    if (input.strategicPillar === "Non Strategic") {
      goalScore = -1;
    }
  }

  // 3. enjoymentScore (range: -3 to +5)
  let enjoymentScore = 0;
  if (input.enjoymentAbility === "Love / Great") enjoymentScore = 5;
  else if (input.enjoymentAbility === "Like / Good") enjoymentScore = 2;
  else if (input.enjoymentAbility === "Don't like / Good") enjoymentScore = -1;
  else if (input.enjoymentAbility === "Don't like / Not Good") enjoymentScore = -3;

  // 4. energyScore (range: -2 to +2)
  const energyScore = input.energyImpact || 0;

  // 5. importanceScore (range: 0 to 3)
  let importanceScore = 0;
  if (input.important && input.urgent) importanceScore = 3;
  else if (input.important && !input.urgent) importanceScore = 2;
  else if (!input.important && input.urgent) importanceScore = 1;

  // 6. complexityModifier (range: -1 to +1)
  let complexityModifier = 0;
  if (input.complexity === "High" && input.enjoymentAbility === "Love / Great") {
    complexityModifier = 1;
  } else if (input.complexity === "High" && input.enjoymentAbility === "Don't like / Not Good") {
    complexityModifier = -1;
  }

  // 7. comboBonuses
  let comboTotal = 0;

  const stronglyCount = (input.goalAlignments || []).filter(
    (g) => g.alignment === "Strongly Aligned" || g.alignment === "Strongly",
  ).length;
  if (input.revenueImpact === "High" && stronglyCount >= 2) {
    comboTotal += 3;
  }

  if (input.enjoymentAbility === "Love / Great" && input.energyImpact === 2) {
    comboTotal += 2;
  }

  if (input.hoursPerMonth > 25 && input.energyImpact < 0) {
    comboTotal -= 2;
  }

  if (input.energyImpact === -2 && input.enjoymentAbility === "Don't like / Not Good") {
    comboTotal -= 3;
  }

  let isDefinitiveDropCombo = false;
  if (
    input.enjoymentAbility === "Don't like / Not Good" &&
    input.revenueImpact === "N/A" &&
    (!input.goalAlignments || input.goalAlignments.length === 0) &&
    input.strategicPillar === "Non Strategic"
  ) {
    comboTotal -= 4;
    isDefinitiveDropCombo = true;
  }

  const total =
    revenueScore +
    goalScore +
    enjoymentScore +
    energyScore +
    importanceScore +
    complexityModifier +
    comboTotal;

  const stage = detectStage(session);
  const delegateThreshold = getDelegateThreshold(stage);

  // Zone-of-Genius flag (α″ gating). CEO-irreducible Strategy & Culture work
  // the founder loves and finds energizing. Computed BEFORE the bucket
  // decision so STEP 3 below can read it. Deliberately omits any total-score
  // precondition: a Strategy & Culture activity rated Love/Great with peak
  // energy is Keep-worthy independent of how the other score components
  // landed (a low total here usually means the activity is goal-light or
  // revenue-mid — neither disqualifies it as founder-only work).
  const isZoneOfGenius =
    input.energyImpact === 2 &&
    input.enjoymentAbility === "Love / Great" &&
    input.strategicPillar === "Strategy & Culture";

  // Decision Logic
  let decision: "DO YOURSELF" | "PLAN" | "DELEGATE" | "DROP" | "MUST_DELEGATE" = "DROP";
  let isUrgentDelegate = false;

  // STEP 1 - OVERRIDE RULE
  if (
    input.important &&
    input.urgent &&
    input.enjoymentAbility &&
    input.enjoymentAbility.includes("Don't like")
  ) {
    decision = "DELEGATE";
    isUrgentDelegate = true;
  } else {
    // STEP 2 - STAGE-AWARE SCORE THRESHOLDS
    // Keep (>=25) and Plan (>=18) thresholds are stage-invariant.
    // Delegate floor is stage-dependent (see getDelegateThreshold above).
    if (total >= 25) decision = "DO YOURSELF";
    else if (total >= 18) decision = "PLAN";
    else if (total >= delegateThreshold) decision = "DELEGATE";
    else decision = "DROP";
  }

  // STEP 3 - ZONE-OF-GENIUS OVERRIDE
  // After threshold-based routing, force Keep on activities flagged as
  // CEO-irreducible Strategy & Culture work. No-op when already Keep.
  // Mutually exclusive with STEP 1's urgent-delegate path: that path
  // requires "Don't like" enjoyment, this flag requires "Love / Great".
  if (isZoneOfGenius && decision !== "DO YOURSELF") {
    decision = "DO YOURSELF";
  }

  // STEP 4 - CEO-IRREDUCIBLE GUARD
  // At enterprise stage, Strategy & Culture pillar activities cannot be
  // Delegate-bucketed regardless of score. They route to Plan — the founder
  // may revisit later as ops mature. Stricter than STEP 3: fires regardless
  // of enjoyment, only at scale. STEP 3 has already moved any Love/Great +
  // energy=+2 Strategy & Culture work to Keep, so this guard only catches
  // the "founder doesn't love it but it's still CEO-shaped" cases.
  if (
    stage === "enterprise" &&
    input.strategicPillar === "Strategy & Culture" &&
    decision === "DELEGATE"
  ) {
    decision = "PLAN";
  }

  // STEP 5 - MUST-DELEGATE GUARD
  // Activities that would Drop but are flagged operationally/legally
  // mandatory route to Must Delegate. The founder may not enjoy the work,
  // but it cannot be eliminated — bookkeeping, compliance, expense
  // approvals, financial review, etc. Must be handed off, not dropped.
  // Only fires when score is below the Drop threshold; a mandatory activity
  // that scores high enough to Delegate or Plan stays in its native bucket.
  if (decision === "DROP" && input.isMandatory === true) {
    decision = "MUST_DELEGATE";
  }

  const breakdown: ScoreBreakdown = {
    revenueScore,
    goalScore,
    enjoymentScore,
    energyScore,
    importanceScore,
    complexityModifier,
    comboTotal,
    total,
  };

  // isZoneOfGenius is computed earlier (before STEP 3 override) so it can
  // feed the bucket decision. Re-use the same value in the flags object.
  const isEnergyDrain = input.energyImpact < 0 && input.hoursPerMonth > 20;
  const isHighHoursBurden = input.hoursPerMonth > 30 && decision === "DELEGATE";
  const isOperationalOnly =
    (!input.goalAlignments || input.goalAlignments.length === 0) &&
    input.strategicPillar !== "Non Strategic";

  const flags: ScoreFlags = {
    isZoneOfGenius,
    isEnergyDrain,
    isHighHoursBurden,
    isUrgentDelegate,
    isOperationalOnly,
    isDefinitiveDrop: isDefinitiveDropCombo,
  };

  const result: ScoreResult = {
    score: total,
    decision,
    breakdown,
    flags,
  };

  if (process.env.NODE_ENV === "development") {
    console.log(`[Score Breakdown] Output:`, result);
  }

  return result;
}

/**
 * Algorithmic Ops Bottleneck Score (0-100).
 *
 * Replaces the prior regex-extraction-from-AI-narrative approach. Computed
 * deterministically from session activity data and the user's routing
 * decisions. Answers: "how much of your time is going to energy-draining
 * low-strategic work, with adjustments for over-extension and active
 * delegation across the portfolio."
 *
 * Formula (β″ percentage normalization):
 *   bottleneck_hours = Σ hours where energy_impact <= -1 AND revenue != "High"
 *   total_hours      = Σ hours across all activities
 *   base 50 + (bottleneck_hours / total_hours) × 50
 *           + 10 if weekly hours > 60 (over-extension)
 *           - 5 if at least 3 strategic pillars have at least one Delegate or
 *               Drop activity (active delegation across the portfolio)
 *   clamp 0..100, integer.
 *
 * Caller is responsible for pre-resolving routing — `resolution` should
 * already reflect force_* / ill_do_it overrides via getFinalResolution.
 */
export interface BottleneckActivity {
  diy_hours_monthly: number;
  energy_impact: number;
  revenue_impact: string;
  strategic_pillar: string | null;
  resolution: string | null; // "Delegate" | "Keep" | "Plan" | "Drop" | null
}

export function computeBottleneckScore(activities: BottleneckActivity[]): number {
  let bottleneckHours = 0;
  let totalHours = 0;
  const pillarsWithDDrop = new Set<string>();

  for (const a of activities) {
    const hrs = Number(a.diy_hours_monthly) || 0;
    totalHours += hrs;
    // ISSUE-3 fix (2026-05-18): restrict drain predicate to Keep-bucket.
    // The diy_hours_monthly field has dual semantics:
    //   - resolution === "Keep": hours the founder still personally does
    //   - resolution === "Delegate"/"Drop"/"MustDelegate": hours moved off
    //     the founder's plate (the /results "Saved/mo" column reads this
    //     value as "hours reclaimed by delegating")
    // The bottleneck score measures the founder's CURRENT personal load —
    // hours already routed off their plate should not count even though
    // they remain populated in diy_hours_monthly (the funnel does not
    // zero the field at delegation; verified empirically on live data).
    // Without this filter, a founder who has already delegated their
    // drain still gets a high bottleneck score, which mis-fires the
    // healthy-lead GATE (bottleneck < 55) for genuinely healthy leads.
    if ((a.energy_impact ?? 0) <= -1 && a.revenue_impact !== "High" && a.resolution === "Keep") {
      bottleneckHours += hrs;
    }
    if (a.resolution === "Delegate" || a.resolution === "Drop" || a.resolution === "MustDelegate") {
      if (a.strategic_pillar) pillarsWithDDrop.add(a.strategic_pillar);
    }
  }

  const bottleneckFraction = bottleneckHours / Math.max(totalHours, 1);
  const weeklyHours = totalHours / 4.33;
  const overExtensionPenalty = weeklyHours > 60 ? 10 : 0;
  const activeDelegationBonus = pillarsWithDDrop.size >= 3 ? -5 : 0;

  const raw = 50 + bottleneckFraction * 50 + overExtensionPenalty + activeDelegationBonus;
  return Math.max(0, Math.min(100, Math.round(raw)));
}
